<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import Table, {
    type ColumnDef,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import TableStepLayout from "@kontextwerk/web-sdk/ui/layout/TableStepLayout.svelte"
  import {
    groupContactPointsByCustomerId,
    sortContactPointsByQuality,
  } from "../../../../../../../_shared/_logic/contact-quality-tags"
  import { isPhoneContactPointKind } from "../../../../../../../_shared/_logic/contact-point-kinds"
  import {
    formatPropertyCustomerRole,
    normalizePropertyCustomerRole,
  } from "../../../../../../../_shared/_logic/customer-roles"
  import { displayScheduleStatusLabel } from "../../../../../../../../../_shared/_logic/status-definitions"
  import WorkOrderTableCell from "../../../work-order-table-cell"
  import type { WorkOrderRow } from "../../../../_logic/work-order-table-model"
  import CancelReplanAppointmentCell from "../../../cancel-replan-appointment-cell"
  import { createStaticRowsRefreshKey } from "../../../../_logic/rows"
  import type {
    ConfirmAppointmentRow,
    ConfirmAppointmentsInit,
    ConfirmAppointmentsWorkflowEvent,
    ConfirmEventsStepOutput,
  } from "../../_shared/_logic/confirm-appointments-contract"
  import { buildConfirmAppointmentRows } from "../../_shared/_logic/rows"
  import {
    confirmAppointmentStatusLabel,
    createConfirmAppointmentFilters,
    filterConfirmAppointmentRows,
    type ConfirmAppointmentFilterState,
  } from "../../_shared/_logic/filters"
  import BatchFilterBar from "../../../../../../../_shared/_modules/batch-filter-bar"
  import {
    batchDateRangeInputValue,
    hasUsefulBatchDateRange,
    hasUsefulBatchSearch,
    hasUsefulBatchSelectOptions,
    normalizeBatchDateRange,
    uniqueBatchOptions,
  } from "../../../../../../../_shared/_logic/filter-values"
  import {
    STABLE_ROW_NUMBER_COLUMN_ID,
    createStableRowNumberState,
    getStableSortedRowPage,
    stableRowNumberColumn,
    stableRowNumberSort,
    withStableRowNumbers,
  } from "../../../../../../../_shared/_logic/stable-row-numbers"
  import ConfirmAppointmentsAction from "./_modules/confirm-appointments-action"
  import type {
    ConfirmAppointmentsActionPhase,
    ConfirmAppointmentsActionResult,
    ConfirmOnsiteContactOption,
  } from "./actions/confirmAppointments"
  import { buildConfirmAppointmentsIntent } from "./build-confirm-appointments-intent"
  import { confirmAppointmentsOperations } from "./operations"

  let {
    init,
    output = null,
    readOnly = false,
    onEvent,
    onError,
  }: {
    init: ConfirmAppointmentsInit
    output?: ConfirmEventsStepOutput | null
    readOnly?: boolean
    onEvent: (event: ConfirmAppointmentsWorkflowEvent) => Promise<void> | void
    onError: (message: string | null) => void
  } = $props()

  let confirmPhase = $state<ConfirmAppointmentsActionPhase>("idle")
  const submitting = $derived(confirmPhase !== "idle")
  let selectedContact = $state("none")
  let customName = $state("")
  let customEmail = $state("")
  let customPhone = $state("")
  let tableFilters = $state<ConfirmAppointmentFilterState>(createConfirmAppointmentFilters())
  const rowNumberState = createStableRowNumberState()

  const rows = $derived(output?.rows ?? buildConfirmAppointmentRows(init))
  const numberedRows = $derived(withStableRowNumbers(rows, rowNumberState))
  const visibleRows = $derived(filterConfirmAppointmentRows(numberedRows, tableFilters))
  const singleRow = $derived(rows.length === 1 ? rows[0] : null)
  const showOnsiteContactSelection = $derived(Boolean(singleRow?.workOrder?.id))
  const refreshKey = $derived(createStaticRowsRefreshKey(visibleRows))
  const statusOptions = $derived(
    uniqueBatchOptions(rows.map(confirmAppointmentStatusLabel), "Alle Status"),
  )
  const appointmentDateValues = $derived(rows.map((row) => row.event.start_at))
  const columns: ColumnDef[] = [
    stableRowNumberColumn(),
    { id: "order", label: "Auftrag" },
    { id: "appointment", label: "Termin" },
    { id: "property", label: "Liegenschaft" },
    { id: "status", label: "Status" },
  ]
  const getRows: TableGetRows = (params) => {
    const { limit, offset } = params
    const sort = (params as { sort?: typeof stableRowNumberSort }).sort

    return getStableSortedRowPage({
      rows: visibleRows,
      columns,
      sort,
      limit,
      offset,
    })
  }
  const orderColumn: ColumnDef = { id: "orderSummary", label: "Auftrag" }
  const propertyColumn: ColumnDef = { id: "propertyLabel", label: "Liegenschaft" }

  const statusLabel = (status?: string | null) => {
    const value = String(status ?? "")
      .trim()
      .toUpperCase()

    if (value === "ANNOUNCED") return "angekündigt"

    if (value === "RESERVED") return "Noch nicht bestätigt"

    if (value === "CONFIRMED") return "bereits bestätigt"

    return displayScheduleStatusLabel(value)
  }

  const resultStatusLabel = (status?: string | null) => {
    const value = String(status ?? "")
      .trim()
      .toUpperCase()

    if (value === "CONFIRMED" || value === "COMPLETED") return "bestätigt"

    if (value === "ALREADY_CONFIRMED") return "bereits bestätigt"

    if (value === "SKIPPED") return "übersprungen"

    if (value === "FAILED" || value === "ERROR") return "fehlgeschlagen"

    if (value === "NOT_FOUND") return "nicht gefunden"

    return "Ergebnis unbekannt"
  }

  const propertyLabel = (row: ConfirmAppointmentRow) => {
    if (row.property?.street)
      return `${row.property.street} ${row.property.house_number ?? ""}`.trim()

    return row.event.location_text || "Liegenschaft"
  }

  const normalizeText = (value: unknown) => String(value ?? "").trim()
  const customersById = $derived(
    new Map((init.customers ?? []).map((customer) => [customer.id, customer])),
  )
  const contactPointsByCustomer = $derived(groupContactPointsByCustomerId(init.contactPoints ?? []))
  const firstPointOfKind = (points: ContactPoint[], kind: ContactPointKind) =>
    sortContactPointsByQuality(points).find((point) => point.kind === kind) ?? null
  const firstPhonePoint = (points: ContactPoint[]) =>
    sortContactPointsByQuality(points).find((point) => isPhoneContactPointKind(point.kind)) ?? null

  const contactOptions = $derived.by<ConfirmOnsiteContactOption[]>(() => {
    const workOrder = singleRow?.workOrder
    const seen = new Set<string>()
    const options: ConfirmOnsiteContactOption[] = []
    for (const recipient of workOrder?.recipients ?? []) {
      const customerId = normalizeText(recipient.customer_id)

      if (!customerId || seen.has(customerId)) continue

      seen.add(customerId)
      const points = contactPointsByCustomer[customerId] ?? []
      const roleLabel = formatPropertyCustomerRole(normalizePropertyCustomerRole(recipient.role))
      const customer = customersById.get(customerId) ?? null
      const label =
        normalizeText(customer?.display_name) ||
        normalizeText(recipient.display_name) ||
        normalizeText(recipient.full_name) ||
        "Unbekannter Ansprechpartner"
      options.push({
        value: `customer:${customerId}`,
        name: `${label} · ${roleLabel}`,
        recipient,
        customer,
        email: firstPointOfKind(points, "EMAIL"),
        phone: firstPhonePoint(points),
      })
    }

    return options
  })

  const selectOptions = $derived([
    { name: "Nicht bekannt", value: "none" },
    ...contactOptions.map((option) => ({ name: option.name, value: option.value })),
    { name: "Andere Person eintragen", value: "custom" },
  ])
  const selectedOption = $derived(
    contactOptions.find((option) => option.value === selectedContact) ?? null,
  )

  const resultForRow = (row: ConfirmAppointmentRow) =>
    output?.items.find((item) => item.event_id === row.event.id) ?? null
  const confirmIntent = $derived(
    buildConfirmAppointmentsIntent({
      rows,
      onsiteContactSelection: {
        enabled: showOnsiteContactSelection,
        value: selectedContact,
        customName,
        customEmail,
        customPhone,
        selectedOption,
      },
    }),
  )

  const updateFilter = (fieldId: string, value: unknown) => {
    tableFilters = {
      ...tableFilters,
      [fieldId]: fieldId === "dateRange" ? normalizeBatchDateRange(value) : String(value ?? ""),
    }
  }

  const handleConfirmResult = async (result: ConfirmAppointmentsActionResult) => {
    await onEvent({ type: "confirm.completed", output: result.output })
  }
</script>

<section class="confirm-step">
  <ConfirmAppointmentsAction
    intent={confirmIntent}
    operations={confirmAppointmentsOperations}
    onSubmissionResult={handleConfirmResult}
    onPhaseChange={(phase) => (confirmPhase = phase)}
  >
    {#snippet children(primaryAction)}
      <TableStepLayout
        titleActions={readOnly ? null : primaryAction}
        title="Zu bestätigende Termine"
        count={visibleRows.length}
        description="Markieren Sie die ausgewählten Termine als bestätigt, wenn der Kunde ihnen zugestimmt hat."
      >
        {#snippet preconfiguration()}
          {#if showOnsiteContactSelection}
            <section class="form light preconfiguration-form" aria-label="Voreinstellungen">
              <ActionBox label="Vorkonfiguration" darkMode={false}>
                <div class="row preconfiguration-fields">
                  <Input
                    type="select"
                    placeholder="Wer ist vor Ort?"
                    options={selectOptions}
                    bind:value={selectedContact}
                    brightBackground={true}
                    disabled={submitting || readOnly}
                  />
                  {#if selectedContact === "custom"}
                    <Input
                      placeholder="Name der Person"
                      bind:value={customName}
                      brightBackground={true}
                      disabled={submitting || readOnly}
                    />
                    <Input
                      placeholder="Telefonnummer"
                      bind:value={customPhone}
                      brightBackground={true}
                      disabled={submitting || readOnly}
                    />
                    <Input
                      placeholder="E-Mail-Adresse"
                      bind:value={customEmail}
                      brightBackground={true}
                      disabled={submitting || readOnly}
                    />
                  {/if}
                </div>
                {#if selectedContact === "none"}
                  <span
                    >Der Termin wird bestätigt, aber es wird noch kein Vor-Ort-Kontakt gespeichert.</span
                  >
                {/if}
              </ActionBox>
            </section>
          {/if}
        {/snippet}

        {#snippet filters()}
          <BatchFilterBar
            fields={[
              {
                id: "search",
                type: "text",
                placeholder: "Suche",
                value: tableFilters.search,
                enabled: hasUsefulBatchSearch(rows.length),
              },
              {
                id: "status",
                type: "select",
                placeholder: "Status",
                value: tableFilters.status,
                options: statusOptions,
                enabled: hasUsefulBatchSelectOptions(statusOptions, rows.length),
              },
              {
                id: "dateRange",
                type: "dateRange",
                placeholder: "Terminzeitraum",
                value: batchDateRangeInputValue(tableFilters.dateRange),
                enabled: hasUsefulBatchDateRange(appointmentDateValues, rows.length),
              },
            ]}
            disabled={submitting || readOnly}
            onChange={updateFilter}
          />
        {/snippet}

        <Table
          {columns}
          {getRows}
          {refreshKey}
          defaultSort={stableRowNumberSort}
          theme="light"
          mode="table-scroll"
          pageSize={80}
          showActions={false}
        >
          {#snippet tableCell({ row, column })}
            {@const item = row as ConfirmAppointmentRow}
            {#if column.id === STABLE_ROW_NUMBER_COLUMN_ID}
              {item[STABLE_ROW_NUMBER_COLUMN_ID]}
            {:else if column.id === "order"}
              {#if item.workOrderRow}
                <WorkOrderTableCell row={item.workOrderRow as WorkOrderRow} column={orderColumn} />
              {:else}
                <span
                  >{item.workOrder?.order_number
                    ? `Auftrag ${item.workOrder.order_number}`
                    : "Auftrag"}</span
                >
              {/if}
            {:else if column.id === "appointment"}
              <CancelReplanAppointmentCell event={item.event} />
            {:else if column.id === "property"}
              {#if item.workOrderRow}
                <WorkOrderTableCell
                  row={item.workOrderRow as WorkOrderRow}
                  column={propertyColumn}
                />
              {:else}
                <span>{propertyLabel(item)}</span>
              {/if}
            {:else if column.id === "status"}
              {@const result = resultForRow(item)}
              <div class="status-cell">
                <strong
                  >{result?.status
                    ? resultStatusLabel(result.status)
                    : statusLabel(item.event.status)}</strong
                >
                {#if result?.detail}
                  <span>{result.detail}</span>
                {/if}
              </div>
            {/if}
          {/snippet}
        </Table>

        {#snippet footer()}
          {#if !readOnly}
            <div class="step-actions">
              {@render primaryAction()}
            </div>
          {/if}
        {/snippet}
      </TableStepLayout>
    {/snippet}
  </ConfirmAppointmentsAction>
</section>

<style>
  .confirm-step {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .preconfiguration-fields {
    padding-top: 1rem;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.8rem 1rem;
  }

  @media (max-width: 70rem) {
    .preconfiguration-fields {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 48rem) {
    .preconfiguration-fields {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .status-cell {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    color: var(--text-invers-100);
  }

  .status-cell span {
    font-size: 0.78rem;
    color: color-mix(in srgb, var(--text-invers-100) 70%, transparent);
  }

  .step-actions {
    display: flex;
    justify-content: flex-end;
  }
</style>
